import React, { useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import axios from '../services/axios';
import {
  formatApiDate,
  formatDateInputValue,
  isValidDateInputValue,
} from '../utils/date';

const GenerarBoletas = () => {
  const [loadingDatos, setLoadingDatos] = useState(true);
  const [loadingMasivo, setLoadingMasivo] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingBoletas, setLoadingBoletas] = useState(false);
  const [loadingFacturaId, setLoadingFacturaId] = useState(null);
  const [loadingFacturaPdfId, setLoadingFacturaPdfId] = useState(null);

  const [clientes, setClientes] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [boletasPeriodo, setBoletasPeriodo] = useState([]);

  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [zonaSeleccionada, setZonaSeleccionada] = useState('todas');
  const [filtroPadron, setFiltroPadron] = useState('');
  const [confirmacionGeneracion, setConfirmacionGeneracion] = useState({
    abierta: false,
    fechaVencimiento: '',
    error: '',
  });

  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    confirmText: 'Aceptar',
    onConfirm: null,
    onCancel: null,
  });

  useEffect(() => {
    const cargarDatos = async () => {
      setLoadingDatos(true);
      try {
        const [clientesRes, zonasRes] = await Promise.all([
          axios.get('/usuarios'),
          axios.get('/zonas'),
        ]);

        const soloClientes = (clientesRes.data || []).filter(u => u.rol === 'cliente');
        setClientes(soloClientes);
        setZonas(zonasRes.data || []);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setModal({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: 'No se pudieron cargar los clientes y zonas.',
          onConfirm: () => setModal((prev) => ({ ...prev, isOpen: false }))
        });
      } finally {
        setLoadingDatos(false);
      }
    };

    cargarDatos();
  }, []);

  useEffect(() => {
    cargarBoletasPeriodo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes, anio, zonaSeleccionada]);

  const obtenerZonaParam = () => {
    if (!zonaSeleccionada || zonaSeleccionada === 'todas') {
      return '';
    }
    return `&zonaId=${zonaSeleccionada}`;
  };

  const cargarBoletasPeriodo = async () => {
    setLoadingBoletas(true);
    try {
      const response = await axios.get(
        `/tarifario/boletas-periodo?mes=${mes}&anio=${anio}${obtenerZonaParam()}`
      );
      setBoletasPeriodo(response.data || []);
    } catch (error) {
      console.error('Error al cargar boletas:', error);
      setBoletasPeriodo([]);
    } finally {
      setLoadingBoletas(false);
    }
  };

  const obtenerZonaLabel = () => {
    if (!zonaSeleccionada || zonaSeleccionada === 'todas') {
      return 'Todas';
    }
    const zona = zonas.find(z => z.id === parseInt(zonaSeleccionada));
    return zona?.nombre || 'Zona seleccionada';
  };

  const estadoBoletaInfo = (estado) => {
    switch (estado) {
      case 'pagada':
        return { label: 'Pagada', className: 'bg-green-100 text-green-700' };
      case 'procesando':
        return { label: 'Procesando', className: 'bg-yellow-100 text-yellow-700' };
      case 'pendiente':
        return { label: 'Pendiente', className: 'bg-orange-100 text-orange-700' };
      case 'vencida':
        return { label: 'Vencida', className: 'bg-red-100 text-red-700' };
      case 'plan_pago':
        return { label: 'Plan de Pago', className: 'bg-indigo-100 text-indigo-700' };
      default:
        return { label: estado, className: 'bg-gray-100 text-gray-600' };
    }
  };

  const calcularFechaVencimientoSugerida = (mesSeleccionado, anioSeleccionado) => {
    let mesVencimiento = Number(mesSeleccionado) + 1;
    let anioVencimiento = Number(anioSeleccionado);

    if (mesVencimiento > 12) {
      mesVencimiento = 1;
      anioVencimiento += 1;
    }

    return formatDateInputValue(new Date(anioVencimiento, mesVencimiento - 1, 10, 12, 0, 0, 0));
  };

  const cerrarConfirmacionGeneracion = () => {
    setConfirmacionGeneracion({
      abierta: false,
      fechaVencimiento: '',
      error: '',
    });
  };

  const handleGenerarMasivo = async () => {
    if (!zonaSeleccionada || zonaSeleccionada === 'todas') {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Zona requerida',
        message: 'Seleccione una zona para generar boletas.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
      return;
    }

    setConfirmacionGeneracion({
      abierta: true,
      fechaVencimiento: calcularFechaVencimientoSugerida(mes, anio),
      error: '',
    });
  };

  const confirmarGeneracionMasiva = async () => {
    if (!isValidDateInputValue(confirmacionGeneracion.fechaVencimiento)) {
      setConfirmacionGeneracion((prev) => ({
        ...prev,
        error: 'Ingrese una fecha de vencimiento válida.',
      }));
      return;
    }

    setLoadingMasivo(true);
    try {
      const response = await axios.post('/tarifario/generar-boletas-masivas', {
        mes: parseInt(mes),
        anio: parseInt(anio),
        zonaId: parseInt(zonaSeleccionada),
        fechaVencimiento: confirmacionGeneracion.fechaVencimiento,
      });

      const resultado = response.data;
      const fechaVencimientoAplicada = confirmacionGeneracion.fechaVencimiento;
      cerrarConfirmacionGeneracion();

      setModal({
        isOpen: true,
        type: 'success',
        title: 'Generación completada',
        message: `Resultados de la generación:\n\nZona: ${obtenerZonaLabel()}\nPeríodo: ${mes}/${anio}\nVencimiento: ${formatApiDate(fechaVencimientoAplicada)}\n\nTotal de clientes: ${resultado.totalClientes}\nBoletas generadas: ${resultado.boletasGeneradas}\nYa existían: ${resultado.boletasExistentes}${resultado.errores?.length ? `\nErrores: ${resultado.errores.length}` : ''}`,
        onConfirm: () => {
          setModal({ ...modal, isOpen: false });
          cargarBoletasPeriodo();
        }
      });
    } catch (error) {
      console.error('Error:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error.response?.data?.message || 'No se pudieron generar las boletas',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
    } finally {
      setLoadingMasivo(false);
    }
  };

  const handleDescargarPdfMasivo = async () => {
    if (boletasPeriodo.length === 0) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Sin boletas',
        message: 'No hay boletas generadas para este período.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
      return;
    }

    setLoadingPdf(true);
    try {
      const response = await axios.get(
        `/tarifario/pdf/boletas-masivas?mes=${mes}&anio=${anio}${obtenerZonaParam()}`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const zonaLabel = obtenerZonaLabel().replace(/\s+/g, '-').toLowerCase();
      link.href = url;
      link.setAttribute('download', `boletas-${zonaLabel}-${mes}-${anio}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setModal({
        isOpen: true,
        type: 'success',
        title: 'PDF generado',
        message: `Se descargó el PDF con ${boletasPeriodo.length} boletas.`,
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
    } catch (error) {
      console.error('Error:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'No se pudo generar el PDF',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleDescargarPdfIndividual = async (boletaId, padron) => {
    try {
      const response = await axios.get(`/tarifario/pdf/boleta/${boletaId}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `boleta-${padron}-${mes}-${anio}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar PDF:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'No se pudo descargar el PDF',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
    }
  };

  const estadoFacturaBadge = (estado) => {
    switch (estado) {
      case 'emitida':
        return 'bg-emerald-100 text-emerald-800';
      case 'error':
        return 'bg-red-100 text-red-700';
      case 'pendiente':
        return 'bg-amber-100 text-amber-800';
      case 'omitida':
        return 'bg-slate-100 text-slate-600';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const estadoFacturaLabel = (estado) => {
    switch (estado) {
      case 'emitida':
        return 'Emitida';
      case 'error':
        return 'Error';
      case 'pendiente':
        return 'Pendiente';
      case 'omitida':
        return 'Omitida';
      default:
        return 'Sin emitir';
    }
  };

  const obtenerNumeroFactura = (boleta) => {
    if (!boleta?.factura_punto_venta || !boleta?.factura_numero) return 'N/A';
    const pto = String(boleta.factura_punto_venta).padStart(4, '0');
    const nro = String(boleta.factura_numero).padStart(8, '0');
    return `${pto}-${nro}`;
  };

  const abrirLogsFactura = (boleta) => {
    const payload = boleta?.factura_payload
      ? JSON.stringify(boleta.factura_payload, null, 2)
      : 'Sin payload registrado';

    const mensaje = [
      `Estado: ${estadoFacturaLabel(boleta?.factura_estado)}`,
      `Comprobante: ${obtenerNumeroFactura(boleta)}`,
      `CAE: ${boleta?.factura_cae || 'N/A'}`,
      `Fecha emisión: ${boleta?.factura_fecha_emision ? formatApiDate(boleta.factura_fecha_emision) : 'N/A'}`,
      `Vto. CAE: ${boleta?.factura_cae_vto ? formatApiDate(boleta.factura_cae_vto) : 'N/A'}`,
      `Error: ${boleta?.factura_error || 'N/A'}`,
      '',
      'Payload:',
      payload,
    ].join('\n');

    setModal({
      isOpen: true,
      type: boleta?.factura_estado === 'error' ? 'error' : 'info',
      title: 'Detalle ARCA',
      message: mensaje,
      onConfirm: () => setModal({ ...modal, isOpen: false }),
    });
  };

  const handleReintentarFactura = (boleta) => {
    if (!boleta?.id) return;
    setModal({
      isOpen: true,
      type: 'warning',
      title: 'Reintentar facturación',
      message: `¿Desea reintentar la emisión de factura para ${boleta.usuario?.nombre || 'el cliente'}?`,
      confirmText: 'Reintentar',
      onConfirm: async () => {
        setModal({ ...modal, isOpen: false });
        setLoadingFacturaId(boleta.id);
        try {
          const response = await axios.post(`/boletas/${boleta.id}/facturar`);
          const estado = response?.data?.estado || 'pendiente';
          setModal({
            isOpen: true,
            type: estado === 'emitida' ? 'success' : 'info',
            title: 'Facturación actualizada',
            message: estado === 'emitida'
              ? 'La factura fue emitida correctamente.'
              : 'Se reintentó la facturación. Revisá el estado en unos segundos.',
            onConfirm: () => {
              setModal({ ...modal, isOpen: false });
              cargarBoletasPeriodo();
            }
          });
        } catch (error) {
          setModal({
            isOpen: true,
            type: 'error',
            title: 'No se pudo reintentar',
            message: error.response?.data?.message || 'Error al reintentar la facturación.',
            onConfirm: () => setModal({ ...modal, isOpen: false })
          });
        } finally {
          setLoadingFacturaId(null);
        }
      },
      onCancel: () => setModal({ ...modal, isOpen: false })
    });
  };

  const handleDescargarFacturaPdf = async (boleta) => {
    if (!boleta?.id) return;
    setLoadingFacturaPdfId(boleta.id);
    try {
      const response = await axios.get(`/tarifario/pdf/factura/${boleta.id}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `factura-${boleta.usuario?.padron || 'cliente'}-${mes}-${anio}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setModal({
        isOpen: true,
        type: 'error',
        title: 'No se pudo descargar',
        message: error.response?.data?.message || 'La factura aún no está emitida.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
    } finally {
      setLoadingFacturaPdfId(null);
    }
  };

  const handleRecalcularBoleta = (boleta) => {
    setModal({
      isOpen: true,
      type: 'info',
      title: 'Recalcular boleta',
      message: `¿Desea recalcular la boleta de ${boleta.usuario?.nombre}?\n\n- Solo boletas pendientes o en proceso\n- No se recalculan boletas pagadas`,
      confirmText: 'Recalcular',
      onConfirm: async () => {
        setModal({ ...modal, isOpen: false });
        setLoadingMasivo(true);

        try {
          await axios.post(`/tarifario/recalcular-boleta/${boleta.id}`);
          setModal({
            isOpen: true,
            type: 'success',
            title: 'Boleta recalculada',
            message: `La boleta de ${boleta.usuario?.nombre} fue recalculada correctamente.`,
            onConfirm: () => {
              setModal({ ...modal, isOpen: false });
              cargarBoletasPeriodo();
            }
          });
        } catch (error) {
          console.error('Error al recalcular:', error);
          setModal({
            isOpen: true,
            type: 'error',
            title: 'Error',
            message: error.response?.data?.message || 'No se pudo recalcular la boleta',
            onConfirm: () => setModal({ ...modal, isOpen: false })
          });
        } finally {
          setLoadingMasivo(false);
        }
      },
      onCancel: () => setModal({ ...modal, isOpen: false })
    });
  };

  const meses = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' },
  ];

  const anios = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 + i);

  const boletasFiltradas = useMemo(() => {
    if (!filtroPadron.trim()) return boletasPeriodo;
    const padronNorm = filtroPadron.trim().toLowerCase();
    return boletasPeriodo.filter((b) =>
      (b.usuario?.padron || '').toLowerCase().includes(padronNorm),
    );
  }, [boletasPeriodo, filtroPadron]);

  const clientesActivos = clientes.filter(c => c.activo !== false && !c.servicio_dado_de_baja);
  const clientesZona = zonaSeleccionada === 'todas'
    ? clientesActivos
    : clientesActivos.filter(c => c.zona?.id === parseInt(zonaSeleccionada));
  const pendientes = Math.max(0, clientesZona.length - boletasPeriodo.length);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 overflow-auto">
        <div className="bg-white border-b">
          <div className="px-8 py-6">
            <h1 className="text-2xl font-bold text-gray-900">Generar Boletas por Zona</h1>
            <p className="text-gray-600 mt-1">Generación masiva y visualización por período y zona</p>
          </div>
        </div>

        <div className="p-8">
          <div className="bg-white rounded-lg border p-6 mb-6">
            <div className="flex flex-wrap gap-4 items-end justify-between">
              <div className="flex flex-wrap gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Zona</label>
                  <select
                    value={zonaSeleccionada}
                    onChange={(e) => setZonaSeleccionada(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loadingDatos}
                  >
                    <option value="todas">Todas</option>
                    {zonas.map((zona) => (
                      <option key={zona.id} value={zona.id}>
                        {zona.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mes</label>
                  <select
                    value={mes}
                    onChange={(e) => setMes(Number(e.target.value))}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loadingMasivo || loadingPdf}
                  >
                    {meses.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Año</label>
                  <select
                    value={anio}
                    onChange={(e) => setAnio(Number(e.target.value))}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loadingMasivo || loadingPdf}
                  >
                    {anios.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Buscar padrón</label>
                  <div className="relative">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none">
                      search
                    </span>
                    <input
                      type="text"
                      value={filtroPadron}
                      onChange={(e) => setFiltroPadron(e.target.value)}
                      placeholder="Ej: 01-0042"
                      className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleGenerarMasivo}
                  disabled={loadingMasivo || loadingPdf}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loadingMasivo ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Generando...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined">flash_on</span>
                      Generar Boletas
                    </>
                  )}
                </button>

                <button
                  onClick={handleDescargarPdfMasivo}
                  disabled={loadingPdf || loadingMasivo || boletasPeriodo.length === 0}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loadingPdf ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Generando PDF...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined">picture_as_pdf</span>
                      Descargar PDF ({boletasPeriodo.length})
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <span className="material-symbols-outlined text-blue-600">groups</span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Clientes en zona</p>
                  <p className="text-2xl font-bold text-gray-900">{clientesZona.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <span className="material-symbols-outlined text-green-600">receipt_long</span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Boletas generadas</p>
                  <p className="text-2xl font-bold text-gray-900">{boletasPeriodo.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                  <span className="material-symbols-outlined text-orange-600">pending</span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Pendientes</p>
                  <p className="text-2xl font-bold text-gray-900">{pendientes}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="px-6 py-4 border-b bg-gray-50">
              <h3 className="font-semibold text-gray-900">
                Boletas de {meses.find(m => m.value === parseInt(mes))?.label} {anio} - Zona {obtenerZonaLabel()}
              </h3>
            </div>

            {loadingBoletas ? (
              <div className="p-12 text-center text-gray-500">Cargando boletas...</div>
            ) : boletasPeriodo.length === 0 ? (
              <div className="p-12 text-center">
                <span className="material-symbols-outlined text-6xl text-gray-300 mb-4">receipt_long</span>
                <p className="text-gray-500 text-lg">No hay boletas generadas para este período</p>
                <p className="text-gray-400 text-sm mt-2">Use el botón "Generar Boletas" para crearlas</p>
              </div>
            ) : boletasFiltradas.length === 0 ? (
              <div className="p-12 text-center">
                <span className="material-symbols-outlined text-6xl text-gray-300 mb-4">search_off</span>
                <p className="text-gray-500 text-lg">No se encontró ningún padrón que coincida con "{filtroPadron}"</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-6 py-3 text-sm font-medium text-gray-600">Padrón</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-gray-600">Cliente</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-gray-600">Zona</th>
                      <th className="text-right px-6 py-3 text-sm font-medium text-gray-600">Consumo</th>
                      <th className="text-right px-6 py-3 text-sm font-medium text-gray-600">Total</th>
                      <th className="text-center px-6 py-3 text-sm font-medium text-gray-600">Estado</th>
                      <th className="text-center px-6 py-3 text-sm font-medium text-gray-600">Factura</th>
                      <th className="text-center px-6 py-3 text-sm font-medium text-gray-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {boletasFiltradas.map((boleta) => {
                      const estadoInfo = estadoBoletaInfo(boleta.estado);
                      return (
                      <tr key={boleta.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {boleta.usuario?.padron}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {boleta.usuario?.nombre}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {boleta.usuario?.zona?.nombre || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 text-right">
                          {boleta.consumo_m3} m³
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
                          ${Number(boleta.total).toLocaleString('es-AR')}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${estadoInfo.className}`}>
                            {estadoInfo.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${estadoFacturaBadge(boleta.factura_estado)}`}>
                            {estadoFacturaLabel(boleta.factura_estado)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleDescargarPdfIndividual(boleta.id, boleta.usuario?.padron)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Descargar PDF"
                            >
                              <span className="material-symbols-outlined">picture_as_pdf</span>
                            </button>

                            <button
                              onClick={() => handleDescargarFacturaPdf(boleta)}
                              disabled={boleta.factura_estado !== 'emitida' || loadingFacturaPdfId === boleta.id}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                              title="Descargar factura"
                            >
                              <span className="material-symbols-outlined">receipt</span>
                            </button>

                            <button
                              onClick={() => abrirLogsFactura(boleta)}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Ver logs ARCA"
                            >
                              <span className="material-symbols-outlined">visibility</span>
                            </button>

                            {!['pagada', 'plan_pago'].includes(boleta.estado) && (
                              <button
                                onClick={() => handleRecalcularBoleta(boleta)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Recalcular boleta"
                              >
                                <span className="material-symbols-outlined">refresh</span>
                              </button>
                            )}

                            <button
                              onClick={() => handleReintentarFactura(boleta)}
                              disabled={boleta.estado !== 'pagada' || boleta.factura_estado === 'emitida' || boleta.factura_estado === 'omitida' || loadingFacturaId === boleta.id}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                              title="Reintentar facturación"
                            >
                              <span className="material-symbols-outlined">sync</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {confirmacionGeneracion.abierta && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-amber-600">event</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Confirmar generación</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Defina la fecha de vencimiento antes de generar las boletas de la zona {obtenerZonaLabel()} para {mes}/{anio}.
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Zona</p>
                  <p className="font-semibold text-gray-900">{obtenerZonaLabel()}</p>
                </div>
                <div>
                  <p className="text-gray-500">Período</p>
                  <p className="font-semibold text-gray-900">
                    {meses.find((m) => m.value === parseInt(mes))?.label} {anio}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Clientes</p>
                  <p className="font-semibold text-gray-900">{clientesZona.length}</p>
                </div>
              </div>
            </div>

            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha de vencimiento
              </label>
              <input
                type="date"
                value={confirmacionGeneracion.fechaVencimiento}
                onChange={(e) => setConfirmacionGeneracion((prev) => ({
                  ...prev,
                  fechaVencimiento: e.target.value,
                  error: '',
                }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loadingMasivo}
              />
              <p className="text-xs text-gray-500 mt-2">
                Sugerida según la lógica actual: día 10 del mes siguiente.
              </p>
              {confirmacionGeneracion.error && (
                <p className="text-sm text-red-600 mt-2">{confirmacionGeneracion.error}</p>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={cerrarConfirmacionGeneracion}
                disabled={loadingMasivo}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarGeneracionMasiva}
                disabled={loadingMasivo}
                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loadingMasivo ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Generando...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined">check_circle</span>
                    Confirmar y generar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={modal.isOpen}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        confirmText={modal.confirmText}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
      />
    </div>
  );
};

export default GenerarBoletas;
